import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { WET_THRESHOLDS, getStatus as getMetricStatus } from './NotificationProvider';

const API_BASE = 'https://birdseye-main-326804163209.asia-southeast1.run.app';
const POLL_INTERVAL_MS = 10 * 60 * 1000;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getReadingScore = (reading, index) => {
  const timestamp = reading?.iso8601_timestamp_string
    ?.replace(' ', 'T')
    .replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3');
  const parsedTime = timestamp ? new Date(timestamp).getTime() : NaN;
  if (!Number.isNaN(parsedTime)) return parsedTime;
  return Number(reading?.id) || index;
};

const getLatestReading = (readings) => (
  readings.reduce((latest, reading, index) => {
    if (!latest) return { reading, score: getReadingScore(reading, index) };
    const score = getReadingScore(reading, index);
    return score > latest.score ? { reading, score } : latest;
  }, null)?.reading ?? null
);

const parseGodJson = (data) => {
  const readings = data?.ammonia?.readings ?? [];
  const latest = getLatestReading(readings);
  return {
    wetPercentage:   latest?.wet_litter_percentage ?? null,
    chickenCount:    latest?.bird_count ?? null,
    latestTimestamp: latest?.iso8601_timestamp_string ?? null,
    nextUpdate:      data?.main?.['expected-time-next-json-update'] ?? null,
    videoUrl:        data?.bloblinks?.latest_processed_video ?? null,
  };
};

const formatTimestamp = (iso) => {
  if (!iso) return null;
  try {
    const normalized = iso.replace(/T(\d{2})-(\d{2})-(\d{2})/, 'T$1:$2:$3');
    return new Date(normalized).toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

// ─── Nav Icons ────────────────────────────────────────────────────────────────

const getCameraStatus = (pct) => {
  if (pct === null) return { label: '—', color: 'text-gray-400' };
  const label = getMetricStatus(pct, WET_THRESHOLDS);
  const color =
    label === 'Safe' ? 'text-green-600' :
    label === 'Moderate' ? 'text-yellow-600' : 'text-red-600';
  return { label, color };
};

const navIcons = {
  Dashboard: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  Cameras: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  History: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="12 8 12 12 14 14"/>
      <path d="M3.05 11a9 9 0 1 0 .5-4.5"/><polyline points="3 3 3 7 7 3"/>
    </svg>
  ),
  Footage: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <path d="M10 11l5 3-5 3v-6z"/>
    </svg>
  ),
  Profile: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Settings: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Team: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="3" />
      <path d="M3 20c1.5-3 4-4.5 6-4.5s4.5 1.5 6 4.5" />
      <path d="M17 11c1 0 2 .8 2 2v1" />
    </svg>
  ),
  Logout: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
};

// ─── Pairing Modal ────────────────────────────────────────────────────────────

const PairingModal = ({ onClose, onPaired }) => {
  const [code, setCode]           = useState('');
  const [status, setStatus]       = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setStatus('loading');
    setErrorMsg('');

    const token = localStorage.getItem('authToken');
    const formattedCode = `${code.slice(0, 3)}-${code.slice(3, 6)}-${code.slice(6)}`;
    try {
      const res = await fetch(
  `${API_BASE}/pairing/Enter_Pairing_Code?pairing_code=${encodeURIComponent(formattedCode)}`,
  {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
);

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail ?? `Error ${res.status}`);
      }

      setStatus('success');
      setTimeout(() => {
        onPaired();
        onClose();
      }, 1200);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message ?? 'Something went wrong. Please try again.');
    }
  };

  // digits-only input auto-formatted as groups of 3: XXX-XXX-XXX
  const handleCodeChange = (e) => {
    const raw = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 9);
    setCode(raw);
    if (status === 'error') setStatus('idle');
  };

  const displayCode =
  code.length === 9
    ? `${code.slice(0, 3)}-${code.slice(3, 6)}-${code.slice(6)}`
    : code;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />

        {/* Dialog */}
        <motion.div
          className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm p-8 flex flex-col items-center"
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        >
          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Icon */}
          <div className="w-16 h-16 rounded-2xl bg-[#AC4242]/10 flex items-center justify-center mb-5">
            <svg className="w-8 h-8 text-[#AC4242]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round"
                d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
            </svg>
          </div>

          <h2 className="text-2xl font-poppins font-bold text-gray-900 mb-1">Pair a Camera</h2>
          <p className="text-sm font-poppins text-gray-500 text-center mb-6 leading-relaxed">
            Enter the 9-character pairing code found on your BirdsEye device label.
          </p>

          <form onSubmit={handleSubmit} className="w-full space-y-4">
            <div className="relative">
              <input
                type="text"
                value={displayCode}
                onChange={handleCodeChange}
                placeholder="ABC-DEF-GHI"
                maxLength={11}
                disabled={status === 'loading' || status === 'success'}
                className={`w-full text-center text-2xl tracking-[0.25em] font-poppins font-bold rounded-2xl border-2 px-4 py-4 outline-none transition-all
                  ${status === 'error'   ? 'border-red-400 bg-red-50 text-red-700 focus:border-red-500' :
                    status === 'success' ? 'border-green-400 bg-green-50 text-green-700' :
                    'border-gray-200 bg-gray-50 text-gray-900 focus:border-[#AC4242] focus:bg-white'}`}
              />
              {status === 'loading' && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="w-5 h-5 border-2 border-[#AC4242]/30 border-t-[#AC4242] rounded-full animate-spin" />
                </div>
              )}
              {status === 'success' && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>

            {status === 'error' && (
              <motion.p
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                className="text-xs font-poppins text-red-600 text-center -mt-1"
              >
                {errorMsg}
              </motion.p>
            )}

            {status === 'success' ? (
              <div className="w-full py-4 rounded-2xl bg-green-500 text-white text-sm font-poppins font-semibold text-center">
                Camera paired successfully!
              </div>
            ) : (
              <button
                type="submit"
                disabled={code.length < 9 || status === 'loading'}
                className="w-full py-4 rounded-2xl bg-[#AC4242] text-white text-sm font-poppins font-semibold
                  transition-all duration-200 hover:bg-[#943636] active:scale-[0.98]
                  disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#AC4242]"
              >
                {status === 'loading' ? 'Pairing…' : 'Pair Camera'}
              </button>
            )}
          </form>

          <p className="text-xs font-poppins text-gray-400 text-center mt-4 leading-relaxed">
            Can't find your code? Check the sticker on the back of your BirdsEye device.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── Camera Card ──────────────────────────────────────────────────────────────

const CameraCard = ({ wetPercentage, chickenCount, latestTimestamp, videoUrl, loading, onClick }) => {
  const [videoError, setVideoError] = useState(false);
  const status = getCameraStatus(wetPercentage);

  return (
    <motion.div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="bg-white rounded-3xl shadow-lg p-5 w-full sm:w-80 cursor-pointer transition-all duration-300 hover:shadow-2xl focus:outline-none focus:ring-2 focus:ring-[#AC4242] focus:ring-offset-2"
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-base font-poppins font-semibold text-gray-800">Camera 1</p>
        {latestTimestamp && (
          <p className="text-xs font-poppins text-gray-400">{formatTimestamp(latestTimestamp)}</p>
        )}
      </div>

      {/* Video feed */}
      <div className="relative bg-gray-100 rounded-xl overflow-hidden aspect-video mb-4">
        {loading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <div className="w-6 h-6 border-2 border-[#AC4242]/30 border-t-[#AC4242] rounded-full animate-spin" />
            <span className="text-xs font-poppins text-gray-400">Loading feed…</span>
          </div>
        ) : videoUrl && !videoError ? (
          <video
            key={videoUrl}
            src={videoUrl}
            autoPlay loop muted playsInline
            className="w-full h-full object-cover"
            onError={() => setVideoError(true)}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M4 8h11a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1z" />
            </svg>
            <span className="text-xs font-poppins text-gray-400">No feed available</span>
          </div>
        )}

        {videoUrl && !videoError && !loading && (
          <>
            <div className="absolute top-2 left-2 bg-[#AC4242] text-white text-xs font-poppins px-2.5 py-1 rounded-lg">
              Zone 1
            </div>
            <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/40 text-white text-xs font-poppins px-2 py-0.5 rounded-md">
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse" />
              LIVE
            </div>
            {wetPercentage !== null && wetPercentage > 15 && (
              <div className="absolute bottom-2 left-2 bg-red-500 text-white px-2.5 py-1 rounded-full font-poppins text-xs flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                Wet litter detected
              </div>
            )}
          </>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center bg-gray-50 rounded-2xl py-3">
          <p className="text-xs font-poppins text-gray-400 mb-1">Wet Litter</p>
          <p className="text-sm font-poppins font-bold text-gray-800">
            {loading ? '—' : wetPercentage !== null ? `${wetPercentage.toFixed(1)}%` : '—'}
          </p>
        </div>
        <div className="text-center bg-gray-50 rounded-2xl py-3">
          <p className="text-xs font-poppins text-gray-400 mb-1">Status</p>
          <p className={`text-sm font-poppins font-semibold ${status.color}`}>
            {loading ? '—' : status.label}
          </p>
        </div>
        <div className="text-center bg-gray-50 rounded-2xl py-3">
          <p className="text-xs font-poppins text-gray-400 mb-1">Birds</p>
          <p className="text-sm font-poppins font-bold text-gray-800">
            {loading ? '—' : chickenCount !== null ? chickenCount : '—'}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Empty State ──────────────────────────────────────────────────────────────

const EmptyState = ({ onAddCamera }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, ease: 'easeOut' }}
    className="flex flex-col items-center justify-center py-20 px-6 text-center"
  >
    {/* Illustrated placeholder */}
    <div className="relative mb-8">
      <div className="w-32 h-32 rounded-3xl bg-[#AC4242]/10 flex items-center justify-center">
        <svg className="w-16 h-16 text-[#AC4242]/40" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.2}>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
          <circle cx="12" cy="13" r="4"/>
        </svg>
      </div>
      {/* decorative dots */}
      <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#AC4242]/20" />
      <div className="absolute -bottom-1 -left-3 w-3 h-3 rounded-full bg-[#AC4242]/15" />
    </div>

    <h2 className="text-2xl font-poppins font-bold text-gray-800 mb-2">No devices yet</h2>
    <p className="text-sm font-poppins text-gray-500 max-w-xs leading-relaxed mb-8">
      Pair your first BirdsEye device to start monitoring your flock in real time.
    </p>

    <button
      onClick={onAddCamera}
      className="flex items-center gap-2.5 px-6 py-3.5 bg-[#AC4242] text-white font-poppins font-semibold text-sm rounded-2xl
        shadow-lg shadow-[#AC4242]/30 transition-all duration-200 hover:bg-[#943636] hover:scale-105 active:scale-[0.98]"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
      </svg>
      Pair a Camera
    </button>
  </motion.div>
);

// ─── Cameras Page ─────────────────────────────────────────────────────────────

const Cameras = () => {
  const navigate = useNavigate();

  const [wetPercentage, setWetPercentage]     = useState(null);
  const [chickenCount, setChickenCount]       = useState(null);
  const [latestTimestamp, setLatestTimestamp] = useState(null);
  const [videoUrl, setVideoUrl]               = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [showPairingModal, setShowPairingModal] = useState(false);
  const [sidebarOpen, setSidebarOpen]         = useState(false);

  // Auth guard
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) navigate('/login');
  }, [navigate]);

  // GodJSON fetch
  const fetchGodJson = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/information/getgodjson`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const parsed = parseGodJson(data);
      setWetPercentage(parsed.wetPercentage);
      setChickenCount(parsed.chickenCount);
      setLatestTimestamp(parsed.latestTimestamp);
      setVideoUrl(parsed.videoUrl);
      // Only set data if camera is already paired via user action
    } catch (err) {
      console.warn('GodJSON fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGodJson();
    const id = setInterval(fetchGodJson, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchGodJson]);

  const handlePaired = () => {
  setLoading(true);
  fetchGodJson();
};

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  const navItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Cameras',   path: '/cameras', active: true },
    { label: 'History',   path: '/history' },
    { label: 'Footage',   path: '/footage' },
    { label: 'Team',      path: '/team'      },
    { label: 'Profile',   path: '/profile' },
    { label: 'Settings',  path: '/settings' },
  ];

  const Sidebar = ({ mobile = false }) => (
    <div className="w-56 flex-shrink-0 p-3 h-screen">
      <div className="h-full min-h-full bg-[#AC4242] rounded-3xl flex flex-col">
        <div className="p-6 flex justify-center">
          <img src="/images/logonav.png" alt="Birdseye Logo" className="h-32 w-auto" />
        </div>

        <nav className="flex-1 px-4 space-y-1">
          {navItems.map(({ label, path, active }) => (
            <button
              key={label}
              onClick={() => { navigate(path); setSidebarOpen(false); }}
              className={`flex items-center gap-3 px-4 py-3 w-full text-left text-white rounded-xl font-poppins text-sm transition-all duration-200 hover:scale-[1.02]
                ${active ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'}`}
            >
              {navIcons[label]}
              {label}
            </button>
          ))}
        </nav>

        <div className="px-4 pb-6">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full text-left text-white/60 hover:text-white hover:bg-white/10 rounded-xl font-poppins text-sm transition-all duration-200"
          >
            {navIcons.Logout}
            Logout
          </button>
        </div>
      </div>
    </div>
  );
  const isPaired = !!videoUrl;

  return (
    <>
      <motion.div
        className="h-screen flex overflow-hidden bg-[#F5EFE7]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      >
        {/* ── Desktop Sidebar ── */}
        <div className="hidden md:block">
          <Sidebar />
        </div>

        {/* ── Mobile Sidebar Drawer ── */}
        <AnimatePresence>
          {sidebarOpen && (
            <>
              <motion.div
                className="fixed inset-0 z-40 bg-black/40 md:hidden"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={() => setSidebarOpen(false)}
              />
              <motion.div
                className="fixed inset-y-0 left-0 z-50 w-64 md:hidden"
                initial={{ x: -260 }} animate={{ x: 0 }} exit={{ x: -260 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              >
                <Sidebar mobile />
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* ── Main Content ── */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">

          {/* Top Bar */}
          <nav className="bg-[#F5EFE7] border-b border-gray-300">
            <div className="px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
              {/* Hamburger on mobile */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden flex items-center justify-center w-9 h-9 rounded-xl hover:bg-gray-200 transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              <img src="/images/loginlogo.png" alt="Birdseye" className="h-10 sm:h-12 w-auto" />
              <div className="text-sm font-poppins text-gray-600 truncate max-w-[160px] sm:max-w-none">
                {localStorage.getItem('userEmail')}
              </div>
            </div>
          </nav>

          {/* Cameras Content */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-3xl sm:text-5xl font-poppins font-semibold text-[#AC4242]">
                Cameras
              </h1>

              {/* Add camera button — always visible in header */}
              {isPaired && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={() => setShowPairingModal(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-2xl border-2 border-[#AC4242] text-[#AC4242] font-poppins text-sm font-semibold
                    transition-all duration-200 hover:bg-[#AC4242] hover:text-white hover:scale-105 active:scale-[0.98]"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="hidden sm:inline">Add Device</span>
                  <span className="sm:hidden">Add</span>
                </motion.button>
              )}
            </div>

            {/* Loading skeleton */}
            {!isPaired ? (
              /* ── Empty / unpaired state ── */
              <EmptyState onAddCamera={() => setShowPairingModal(true)} />
            ) : loading ? (
              <div className="flex flex-wrap gap-6">
                <div className="w-full sm:w-80 bg-white rounded-3xl shadow-lg p-5 animate-pulse">
                  <div className="h-4 bg-gray-100 rounded w-24 mb-3" />
                  <div className="aspect-video bg-gray-100 rounded-xl mb-4" />
                  <div className="grid grid-cols-3 gap-3">
                    {[0,1,2].map(i => <div key={i} className="bg-gray-100 rounded-2xl py-8" />)}
                  </div>
                </div>
              </div>
            ) : (
              /* ── Camera grid ── */
              <div className="flex flex-wrap gap-6 items-start">
                <CameraCard
                  wetPercentage={wetPercentage}
                  chickenCount={chickenCount}
                  latestTimestamp={latestTimestamp}
                  videoUrl={videoUrl}
                  loading={false}
                  onClick={() => navigate('/dashboard')}
                />

                {/* Add more camera tile */}
                <motion.button
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.15 }}
                  onClick={() => setShowPairingModal(true)}
                  className="w-full sm:w-80 flex-shrink-0 aspect-[4/3] sm:aspect-auto sm:h-64 mt-0 rounded-3xl border-2 border-dashed border-[#AC4242]/40
                    flex flex-col items-center justify-center gap-3 text-[#AC4242]/60
                    transition-all duration-200 hover:border-[#AC4242] hover:text-[#AC4242] hover:bg-[#AC4242]/5 hover:scale-[1.02]"
                >
                  <div className="w-12 h-12 rounded-2xl border-2 border-current flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <span className="font-poppins text-sm font-semibold">Add Device</span>
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* ── Pairing Modal ── */}
      {showPairingModal && (
        <PairingModal
          onClose={() => setShowPairingModal(false)}
          onPaired={handlePaired}
        />
      )}
    </>
  );
};

export default Cameras;
